<template>
  <div class="tour-preview" v-if="!started">
    <div class="preview-bg" :style="bgStyle">
      <div class="preview-overlay"></div>
    </div>

    <div class="preview-content">
      <!-- Logo -->
      <div class="preview-logo" v-if="branding?.logo_url">
        <img :src="branding.logo_url" alt="" />
      </div>

      <!-- Play Button -->
      <button class="play-button" @click="$emit('start')">
        <div class="play-circle">
          <svg width="36" height="36" viewBox="0 0 24 24" fill="white">
            <path d="M8 5v14l11-7z"/>
          </svg>
        </div>
      </button>

      <!-- Tour Info -->
      <div class="preview-info">
        <h1 class="preview-title">{{ title }}</h1>
        <p v-if="description && description !== 'null'" class="preview-description">{{ description }}</p>
        <div class="preview-meta">
          <span v-if="sceneCount" class="meta-item">
            <svg width="14" height="14" viewBox="0 0 24 24" fill="currentColor">
              <path d="M21 19V5c0-1.1-.9-2-2-2H5c-1.1 0-2 .9-2 2v14c0 1.1.9 2 2 2h14c1.1 0 2-.9 2-2zM8.5 13.5l2.5 3.01L14.5 12l4.5 6H5l3.5-4.5z"/>
            </svg>
            {{ sceneCount }} scenes
          </span>
          <span class="meta-item">
            <svg width="14" height="14" viewBox="0 0 24 24" fill="currentColor">
              <path d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm-1 17.93c-3.95-.49-7-3.85-7-7.93 0-.62.08-1.21.21-1.79L9 15v1c0 1.1.9 2 2 2v1.93zm6.9-2.54c-.26-.81-1-1.39-1.9-1.39h-1v-3c0-.55-.45-1-1-1H8v-2h2c.55 0 1-.45 1-1V7h2c1.1 0 2-.9 2-2v-.41c2.93 1.19 5 4.06 5 7.41 0 2.08-.8 3.97-2.1 5.39z"/>
            </svg>
            360° Virtual Tour
          </span>
        </div>
      </div>

      <!-- Powered by -->
      <div class="preview-footer">
        <span>Powered by <strong>VirtualTourEasy</strong></span>
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import { computed } from 'vue'

const props = defineProps<{
  title: string
  description?: string | null
  coverImage?: string | null
  sceneCount: number
  started: boolean
  branding?: {
    logo_url?: string
    company_name?: string
  } | null
}>()

defineEmits<{ start: [] }>()

const bgStyle = computed(() => {
  if (props.coverImage) {
    return { backgroundImage: `url(${props.coverImage})` }
  }
  return { background: 'linear-gradient(135deg, #0f172a 0%, #1e3a5f 50%, #0f172a 100%)' }
})
</script>

<style scoped>
.tour-preview {
  position: absolute;
  inset: 0;
  z-index: 50;
  display: flex;
  align-items: center;
  justify-content: center;
}

.preview-bg {
  position: absolute;
  inset: 0;
  background-size: cover;
  background-position: center;
}

.preview-overlay {
  position: absolute;
  inset: 0;
  background: rgba(0, 0, 0, 0.55);
  backdrop-filter: blur(2px);
}

.preview-content {
  position: relative;
  z-index: 1;
  display: flex;
  flex-direction: column;
  align-items: center;
  text-align: center;
  padding: 32px;
  max-width: 600px;
}

.preview-logo {
  margin-bottom: 24px;
}

.preview-logo img {
  max-height: 48px;
  max-width: 200px;
  object-fit: contain;
}

.play-button {
  background: none;
  border: none;
  cursor: pointer;
  margin-bottom: 32px;
  padding: 0;
}

.play-circle {
  width: 80px;
  height: 80px;
  border-radius: 50%;
  background: rgba(59, 130, 246, 0.9);
  display: flex;
  align-items: center;
  justify-content: center;
  padding-left: 4px; /* visual center for play icon */
  box-shadow: 0 4px 24px rgba(59, 130, 246, 0.4);
  transition: all 0.3s;
}

.play-button:hover .play-circle {
  transform: scale(1.1);
  background: rgba(59, 130, 246, 1);
  box-shadow: 0 8px 32px rgba(59, 130, 246, 0.6);
}

.preview-info {
  margin-bottom: 32px;
}

.preview-title {
  font-size: 28px;
  font-weight: 700;
  color: white;
  margin: 0 0 12px;
  line-height: 1.3;
  text-shadow: 0 2px 8px rgba(0, 0, 0, 0.3);
}

.preview-description {
  color: rgba(255, 255, 255, 0.8);
  font-size: 15px;
  margin: 0 0 16px;
  line-height: 1.5;
}

.preview-meta {
  display: flex;
  gap: 16px;
  justify-content: center;
  flex-wrap: wrap;
}

.meta-item {
  display: flex;
  align-items: center;
  gap: 6px;
  color: rgba(255, 255, 255, 0.6);
  font-size: 13px;
}

.preview-footer {
  color: rgba(255, 255, 255, 0.4);
  font-size: 12px;
}

.preview-footer strong {
  color: rgba(255, 255, 255, 0.6);
}

@media (max-width: 768px) {
  .preview-title { font-size: 22px; }
  .play-circle { width: 64px; height: 64px; }
  .play-circle svg { width: 28px; height: 28px; }
  .preview-content { padding: 20px; }
}
</style>
